import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button, ListGroup, Spinner } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { useChatContext, useChannelMembersState } from "stream-chat-react";
import { useStaffChat } from "./StaffChatProvider";
import useOrgUsers from "./useOrgUsers";
import { getInitials, selectInModalProps } from "./chatUtils";

const GROUP_NAME_MAX_LENGTH = 60;

const greenButtonStyle = { backgroundColor: "#055837", color: "#ffcc01" };

// Members of a group chat: list, add, remove, rename and leave. The title is the group name.
// The buttons are shown from Stream's own answer (own_capabilities), not from the portal roles.
// readOnly: Super Admin looking at a group he is not a member of (list only).
// canManage: false in the group chat only mode (no rename, add people, remove, leave group).
const GroupMembersModal = ({
  show,
  onHide,
  channel,
  readOnly = false,
  canManage = true,
}) => {
  const { currentUserId, currentUserName } = useStaffChat();
  const { setActiveChannel } = useChatContext();
  const membersState = useChannelMembersState(channel);

  const capabilities = channel?.data?.own_capabilities || [];
  const canUpdateMembers =
    !readOnly && canManage && capabilities.includes("update-channel-members");
  const canRename =
    !readOnly && canManage && capabilities.includes("update-channel");
  const canLeave =
    !readOnly && canManage && capabilities.includes("leave-channel");

  const { users, loading: loadingUsers } = useOrgUsers(
    show && canUpdateMembers
  );

  const [, setChannelVersion] = useState(0);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [peopleToAdd, setPeopleToAdd] = useState([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset every time the modal is opened
  useEffect(() => {
    if (show && channel) {
      setRenaming(false);
      setNewName(channel.data?.name || "");
      setPeopleToAdd([]);
      setConfirmRemoveId(null);
      setConfirmLeave(false);
      setBusy(false);
    }
  }, [show, channel]);

  // Group renamed by someone else while the modal is open
  useEffect(() => {
    if (!show || !channel) {
      return;
    }
    const listener = channel.on("channel.updated", () => {
      setChannelVersion((version) => version + 1);
    });
    return () => {
      listener.unsubscribe();
    };
  }, [show, channel]);

  const createdById = channel?.data?.created_by?.id;

  const members = useMemo(() => {
    const list = Object.values(membersState || {}).map((member) => {
      const userId = member.user_id || member.user?.id;
      return {
        id: userId,
        name: member.user?.name || userId,
        online: member.user?.online === true,
        isCreator: userId === createdById,
        isModerator: member.channel_role === "channel_moderator",
      };
    });

    list.sort((a, b) => {
      if (a.isCreator !== b.isCreator) {
        return a.isCreator ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });

    return list;
  }, [membersState, createdById]);

  const addOptions = useMemo(() => {
    const memberIds = members.map((member) => member.id);
    return users
      .filter((user) => !memberIds.includes(user.id))
      .map((user) => ({ value: user.id, label: user.name || user.id }));
  }, [users, members]);

  if (!channel) {
    return null;
  }

  const groupName = channel.data?.name || "Group";

  const renameGroup = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (name === "" || busy) {
      return;
    }
    if (name === groupName) {
      setRenaming(false);
      return;
    }
    setBusy(true);
    try {
      await channel.updatePartial({ set: { name: name } });
      setRenaming(false);
      toast.success("Group renamed.");
    } catch (error) {
      console.error("Error renaming group:", error);
      toast.error("Unable to rename the group.");
    } finally {
      setBusy(false);
    }
  };

  const addPeople = async () => {
    if (peopleToAdd.length === 0 || busy) {
      return;
    }
    setBusy(true);
    try {
      const names = peopleToAdd.map((person) => person.label).join(", ");
      await channel.addMembers(
        peopleToAdd.map((person) => person.value),
        { text: `${currentUserName} added ${names}` }
      );
      setPeopleToAdd([]);
      toast.success("Members added.");
    } catch (error) {
      console.error("Error adding members:", error);
      toast.error("Unable to add the members.");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (member) => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await channel.removeMembers([member.id], {
        text: `${currentUserName} removed ${member.name}`,
      });
      setConfirmRemoveId(null);
      toast.success("Member removed.");
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Unable to remove the member.");
    } finally {
      setBusy(false);
    }
  };

  const leaveGroup = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await channel.removeMembers([currentUserId], {
        text: `${currentUserName} left the group`,
      });
      setActiveChannel(undefined);
      onHide();
    } catch (error) {
      console.error("Error leaving group:", error);
      toast.error("Unable to leave the group.");
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title
          className="text-break"
          style={{
            fontSize: "18px",
            fontFamily: "Arial, sans-serif",
            color: "#055837",
          }}
        >
          {groupName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ fontFamily: "Arial, sans-serif" }}>
        {/* Rename (the group name itself is the title of the popup) */}
        {!renaming && canRename && (
          <div className="d-flex justify-content-end mb-3">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={busy}
              onClick={() => {
                setNewName(groupName);
                setRenaming(true);
              }}
            >
              Rename group
            </Button>
          </div>
        )}

        {renaming && (
          <Form onSubmit={renameGroup} className="d-flex mb-3" style={{ gap: "8px" }}>
            <Form.Control
              type="text"
              value={newName}
              maxLength={GROUP_NAME_MAX_LENGTH}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
            <Button
              type="submit"
              size="sm"
              variant="light"
              style={greenButtonStyle}
              disabled={busy || newName.trim() === ""}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={busy}
              onClick={() => setRenaming(false)}
            >
              Cancel
            </Button>
          </Form>
        )}

        {/* Members */}
        <div className="text-muted text-start mb-1">
          Members ({members.length})
        </div>
        <ListGroup variant="flush" className="staff-chat-user-list mb-3">
          {members.map((member) => (
            <ListGroup.Item
              key={member.id}
              className="d-flex align-items-center"
            >
              <span className="staff-chat-user-avatar">
                {getInitials(member.name)}
                {member.online && (
                  <span className="staff-chat-user-online-dot"></span>
                )}
              </span>
              <span className="flex-grow-1 text-start">
                {member.name}
                {member.id === currentUserId && " (You)"}
                {member.isCreator && (
                  <small className="text-muted ms-2">Creator</small>
                )}
                {!member.isCreator && member.isModerator && (
                  <small className="text-muted ms-2">Admin</small>
                )}
              </span>

              {canUpdateMembers &&
                member.id !== currentUserId &&
                confirmRemoveId !== member.id && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={busy}
                    onClick={() => setConfirmRemoveId(member.id)}
                  >
                    Remove
                  </Button>
                )}

              {canUpdateMembers && confirmRemoveId === member.id && (
                <span className="d-flex" style={{ gap: "6px" }}>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => removeMember(member)}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={busy}
                    onClick={() => setConfirmRemoveId(null)}
                  >
                    Cancel
                  </Button>
                </span>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>

        {/* Add people */}
        {canUpdateMembers && (
          <div>
            <div className="text-muted text-start mb-1">Add people</div>
            <div className="d-flex" style={{ gap: "8px" }}>
              <div className="flex-grow-1">
                <Select
                  isMulti
                  options={addOptions}
                  value={peopleToAdd}
                  onChange={(selected) => setPeopleToAdd(selected || [])}
                  isLoading={loadingUsers}
                  isDisabled={busy}
                  placeholder="Select staff"
                  noOptionsMessage={() => "No more staff to add."}
                  closeMenuOnSelect={false}
                  {...selectInModalProps}
                />
              </div>
              <Button
                variant="light"
                style={greenButtonStyle}
                disabled={busy || peopleToAdd.length === 0}
                onClick={addPeople}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>

      {canLeave && (
        <Modal.Footer className="d-flex justify-content-end">
          <div className="d-flex align-items-center" style={{ gap: "6px" }}>
            {busy && <Spinner animation="border" size="sm" />}

            {canLeave && !confirmLeave && (
              <Button
                size="sm"
                variant="outline-danger"
                disabled={busy}
                onClick={() => setConfirmLeave(true)}
              >
                Leave group
              </Button>
            )}

            {canLeave && confirmLeave && (
              <>
                <span className="text-danger">Leave this group?</span>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy}
                  onClick={leaveGroup}
                >
                  Yes, leave
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={busy}
                  onClick={() => setConfirmLeave(false)}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default GroupMembersModal;
